import AttReportByDateBtn from "components/Pdfs/Attendance/AttByDayAllUsers/AttReportByDateBtn";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  getCurTimeOfDeviceTimeZone,
  getDayNameByDate,
  getTotalHours,
  getTotalLateTime,
  getTotalOvertime,
} from "utils/attendanceUtility";
import {
  getNightUserTotalHours,
  getNightUserTotalLateTime,
  getNightUserTotalOvertime,
} from "utils/NightUserAttendanceUtility";

export default function GenerateAttendanceByDate({ deviceId }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  /*   All States
   ********************************************* */ const [
    showModal,
    setShowModal,
  ] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [departments, setDepartments] = useState([]);

  /*   All Functions
   ********************************************* */
  const loadingOn = useCallback(() => {
    setLoading(true);
  }, []);

  const loadingOff = useCallback(() => {
    setLoading(false);
  }, []);

  const handleSelectDate = (event) => {
    setSelectedReport(null);
    setSelectedDate(event.target.value);
  };

  const handleSelectAllUsers = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allSelected = filteredUsers.map((user) => user.userId);
      setSelectedUsers(allSelected);
      // if (filterDepartment) {
      //   // Select all filtered users
      //   const departmentUserIds = filteredUsers.map((user) => user.userId);
      //   // Keep previously selected users that aren't in the current filtered view
      //   const otherSelectedUsers = selectedUsers.filter(
      //     (id) =>
      //       !allUsers.find((user) => user.userId === id)?.department ||
      //       allUsers.find((user) => user.userId === id)?.department !==
      //         filterDepartment
      //   );
      //   setSelectedUsers([...otherSelectedUsers, ...departmentUserIds]);
      // } else {
      //   // Select all users if no department filter
      //   setSelectedUsers(allUsers.map((user) => user.userId));
      // }
    } else {
      if (filterDepartment) {
        // Deselect only users from current department
        const departmentUserIds = filteredUsers.map((user) => user.userId);
        setSelectedUsers((prevSelected) =>
          prevSelected.filter((id) => !departmentUserIds.includes(id))
        );
      } else {
        // Deselect all users if no department filter
        setSelectedUsers([]);
      }
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        // Deselect user
        const newSelected = prev.filter((id) => id !== userId);
        if (newSelected.length === 0) {
          setSelectAll(false);
        }
        return newSelected;
      } else {
        // Select user
        const newSelected = [...prev, userId];
        if (newSelected.length === allUsers.length) {
          setSelectAll(true);
        }
        return newSelected;
      }
    });
  };

  const getUsersDataByDeviceId = useCallback(
    async (id) => {
      loadingOn();

      let filterBy = searchParams.get("filterBy") || "active";

      try {
        const { data } = await API.post(
          "/device-users-list/" + id,
          { filterBy },
          {
            headers: authHeader(),
          }
        );

        if (data?.success) {
          if (data?.data && data?.data.length > 0) {
            setAllUsers(data.data);
            // Select all users by default
            setSelectedUsers(data.data.map((user) => user.userId));

            // Extract unique departments
            const uniqueDepartments = [
              ...new Set(data.data.map((user) => user.department)),
            ];
            setDepartments(uniqueDepartments);

            setSelectedReport(null);
          }
        }

        loadingOff();
      } catch (err) {
        loadingOff();
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      } finally {
        loadingOff();
      }
    },
    [dispatch, loadingOff, loadingOn, searchParams, setSelectedReport]
  );

  // Get Device Id
  const GetDeviceById = useCallback(async () => {
    try {
      if (!deviceId) {
        return;
      }
      loadingOn();
      const { data } = await API.get("/get-attendance-device/" + deviceId, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedDevice(data.data);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  }, [deviceId, dispatch, loadingOff, loadingOn]);

  // Make Api call to get logs for all users by selected date
  const getLogsByDateForAllUsers = async (selectedDate) => {
    try {
      loadingOn();

      if (!selectedDevice?.id || !selectedDevice?.timezone) {
        dispatch(
          setNotification({
            message: "Device not found or timezone not set",
            type: "error",
          })
        );
        loadingOff();
        return;
      }

      const pakistanStartDate = new Date(
        selectedDate.toLocaleString("en-US", {
          timeZone: selectedDevice?.timezone,
        })
      );

      // set hours to 0, minutes to 0, seconds to 0
      pakistanStartDate.setUTCHours(0, 0, 0, 0);
      const pakistanEndDate = new Date(pakistanStartDate);
      pakistanEndDate.setUTCHours(23, 59, 59, 999);
      //Add one day to end date
      pakistanEndDate.setDate(pakistanEndDate.getDate() + 1);

      const { data } = await API.post(
        "/user-logs/selected-date",
        {
          deviceId,
          startDate: pakistanStartDate,
          endDate: pakistanEndDate,
          userIds: selectedUsers, // Pass selected user IDs to the API
        },
        {
          headers: authHeader(),
        }
      );

      loadingOff();
      return data;
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Generate report for the selected date
  const GenerateReport = async (e) => {
    setSelectedReport(null);

    if (!selectedDate) {
      dispatch(
        setNotification({
          message: "Please Select Date",
          type: "error",
        })
      );
      return;
    }

    if (selectedUsers.length === 0) {
      dispatch(
        setNotification({
          message: "Please select at least one user",
          type: "error",
        })
      );
      return;
    }

    // Make API call for the selected date
    const data = await getLogsByDateForAllUsers(selectedDate);

    if (data?.success) {
      processUsersAttendanceData(data.data, data?.holidaysList);
    } else {
      dispatch(
        setNotification({
          message: data?.message,
          type: "error",
        })
      );
    }
  };

  const getFormattedTime = (dbDate) => {
    const date = new Date(dbDate);
    // Convert to proper UTC time format without using local time
    const hours = date.getUTCHours().toString().padStart(2, "0");
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    const seconds = date.getUTCSeconds().toString().padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };

  const getFormattedDate = (dbDate) => {
    const date = new Date(dbDate);
    return `${
      date.getUTCMonth() + 1
    }-${date.getUTCDate()}-${date.getUTCFullYear()}`;
  };

  const formatUtcTime = (isoString) => {
    if (!isoString) return "";
    const dt = new Date(isoString);
    const hours = String(dt.getUTCHours()).padStart(2, "0");
    const minutes = String(dt.getUTCMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const buildNightShiftSessions = (logs = []) => {
    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.recordTime) - new Date(b.recordTime)
    );

    const dateGroups = {};

    sortedLogs.forEach((log) => {
      const dt = new Date(log.recordTime);
      const hour = dt.getUTCHours();
      const rawDate = log.recordTime.slice(0, 10);

      if (!dateGroups[rawDate]) {
        dateGroups[rawDate] = {
          inRecords: [],
          outRecords: [],
        };
      }

      if (hour >= 12) {
        dateGroups[rawDate].inRecords.push(log);
      } else {
        dateGroups[rawDate].outRecords.push(log);
      }
    });

    const sessionsMap = {};

    Object.entries(dateGroups).forEach(([date, group]) => {
      if (group.inRecords.length > 0) {
        group.inRecords.sort(
          (a, b) => new Date(a.recordTime) - new Date(b.recordTime)
        );
        const earliestIn = group.inRecords[0];

        if (!sessionsMap[date]) {
          sessionsMap[date] = {
            date,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[date].in = earliestIn.recordTime;
        sessionsMap[date].inTimeRecordId = earliestIn.id;
        sessionsMap[date].inLog = earliestIn;
        sessionsMap[date].inRecords = group.inRecords;
      }

      if (group.outRecords.length > 0) {
        group.outRecords.sort(
          (a, b) => new Date(b.recordTime) - new Date(a.recordTime)
        );
        const latestOut = group.outRecords[0];

        const prevDateObj = new Date(date + "T00:00:00Z");
        prevDateObj.setUTCDate(prevDateObj.getUTCDate() - 1);
        const prevDate = prevDateObj.toISOString().slice(0, 10);

        if (!sessionsMap[prevDate]) {
          sessionsMap[prevDate] = {
            date: prevDate,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[prevDate].out = latestOut.recordTime;
        sessionsMap[prevDate].outTimeRecordId = latestOut.id;
        sessionsMap[prevDate].outLog = latestOut;
        sessionsMap[prevDate].outRecords = group.outRecords;
      }
    });

    return Object.values(sessionsMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
  };

  const getNightUserRecordForSelectedDate = ({
    logs = [],
    selectedUser,
    selectedDevice,
    selectedDate,
  }) => {
    if (!logs.length || !selectedUser?.weeklySchedule) {
      return null;
    }

    const sessions = buildNightShiftSessions(logs);
    const selectedDateKey = new Date(selectedDate).toISOString().slice(0, 10);
    const session = sessions.find((sess) => sess.date === selectedDateKey);

    if (!session || !session.in || !session.out) {
      return null;
    }

    const weeklySchedule = JSON.parse(selectedUser?.weeklySchedule || "{}");
    const rules = JSON.parse(selectedUser?.rules || "{}");

    const dateReference = new Date(session.in);
    const dayNameOfWeek = getDayNameByDate(dateReference);
    const dayOfMonth = dateReference.getUTCDate();

    const userInTime = weeklySchedule[dayNameOfWeek]?.inTime || "";
    const userOutTime = weeklySchedule[dayNameOfWeek]?.outTime || "";

    const inTime = formatUtcTime(session.in);
    const outTime = formatUtcTime(session.out);

    const sessionLogs = [
      ...(session.inRecords || []),
      ...(session.outRecords || []),
    ];

    const shouldCalculateOvertime = sessionLogs.some(
      (log) => log.calculateOverTime === false
    )
      ? false
      : true;

    const shouldCalculateLateTime = sessionLogs.some(
      (log) => log.calculateLateTime === false
    )
      ? false
      : true;

    const totalHoursValue =
      inTime && outTime
        ? parseFloat(getNightUserTotalHours(inTime, outTime) || 0.0)
        : 0.0;

    const totalLateTimeValue =
      inTime && outTime
        ? parseFloat(
            getNightUserTotalLateTime(
              inTime,
              outTime,
              userInTime,
              userOutTime,
              selectedUser?.relaxationMinutes,
              rules,
              selectedDevice
            ) || 0.0
          )
        : 0.0;

    const overTimeValue =
      inTime && outTime
        ? parseFloat(
            getNightUserTotalOvertime(
              inTime,
              outTime,
              userInTime,
              userOutTime,
              selectedUser?.overtimeAfterMinutes,
              rules,
              selectedDevice
            ) || 0.0
          )
        : 0.0;

    const totalExpectedHours =
      userInTime && userOutTime
        ? parseFloat(getNightUserTotalHours(userInTime, userOutTime) || 0.0)
        : 0.0;

    return {
      inTime,
      outTime,
      shouldCalculateOvertime,
      shouldCalculateLateTime,
      formattedDate: getFormattedDate(session.in),
      dayOfWeek: dayOfMonth,
      inRecord: session.in,
      outRecord: session.out,
      dayNameOfWeek,
      userInTime,
      userOutTime,
      totalExpectedHours,
      totalLateTime: inTime === outTime ? 0.0 : totalLateTimeValue,
      totalHours: inTime === outTime ? 0.0 : totalHoursValue,
      overTime: inTime === outTime ? 0.0 : overTimeValue,
    };
  };

  const processRecordByDay = (logs) => {
    const recordsByDay = [];
    /* 
    Looping Over Attendance records arr received by database
    1.Getting Date and Time from timestamp of database and custom formatting it.
    2.Appending to records by day: { day : records-of-that-day } format.
     */

    logs.forEach((record) => {
      const date = new Date(record.recordTime);

      const formattedTime = getFormattedTime(record.recordTime);

      // Get date in UTC for consistent date handling
      const formattedDate = getFormattedDate(record.recordTime);
      // Properly determine day of week based on UTC date
      recordsByDay.push({
        ...record,
        formattedTime,
        formattedDate,
        dayOfWeek: getDayNameByDate(date),
      });
    });

    return recordsByDay;
  };

  const processUsersAttendanceData = (dbLogs, holidaysList) => {
    /*   Step 1: Group logs of selected Users by group key department => user name
     ********************************************* */

    console.log("dbLogs", dbLogs);

    const groupedLogs = dbLogs.reduce((acc, log) => {
      if (selectedUsers.includes(log.userId)) {
        // get user from allUsers array
        const currUser = allUsers.find((user) => user.userId === log.userId);

        if (!acc[currUser?.department]) {
          acc[currUser?.department] = {};
        }
        if (!acc[currUser?.department][currUser?.name]) {
          acc[currUser?.department][currUser?.name] = { logs: [] };
          acc[currUser?.department][currUser?.name]["userData"] = currUser;
          acc[currUser?.department][currUser?.name]["userId"] =
            currUser?.userId;
        }

        // Push log to the user's logs array
        acc[currUser?.department][currUser?.name]["logs"].push(log);
      }
      return acc;
    }, {});

    console.log("groupedLogs", groupedLogs);

    // check if its holiday
    const isHoliday = holidaysList.some((holiday) => {
      return getFormattedDate(holiday.date) === getFormattedDate(selectedDate);
    });

    // console.log("Grouped Logs:", groupedLogs);
    /*   Step 2: Process each user's logs to calculate attendance details
     ********************************************* */
    const processedData = Object.entries(groupedLogs).reduce(
      (acc, [department, users]) => {
        Object.entries(users).forEach(([userName, userData]) => {
          const { logs } = userData;
          if (logs.length > 0) {
            // Process logs for each user
            const selectedUser = userData.userData;

            let processedAttendance = null;
            if (selectedUser?.isNightOffice) {
              processedAttendance = getNightUserRecordForSelectedDate({
                logs,
                selectedUser,
                selectedDevice,
                selectedDate,
              });
            } else {
              const processedLogs = processRecordByDay(logs);
              processedAttendance = getTimeCalculations(
                processedLogs,
                selectedUser
              );
            }

            if (!processedAttendance) {
              return;
            }

            /*   Check Overtime
             **************** */
            let overTime = isHoliday
              ? processedAttendance?.totalHours
              : processedAttendance?.overTime;
            if (processedAttendance?.shouldCalculateOvertime === false) {
              overTime = 0.0;
            }

            /*   Check Late Time
             **************** */
            let totalLateTime = 0.0;
            if (processedAttendance?.shouldCalculateLateTime === false) {
              totalLateTime = 0.0;
            } else {
              totalLateTime = processedAttendance?.totalLateTime || 0.0;
            }

            // Add processed data to the accumulator
            acc.push({
              department,
              userName,
              userId: userData.userId,

              attendance: {
                ...processedAttendance,
                type: isHoliday ? "Holiday" : "Present",
                message: isHoliday ? "Holiday" : "Present",
                overTime: overTime,
                totalLateTime: isHoliday ? 0.0 : totalLateTime,
              },
              userData: selectedUser,
            });
          }
        });
        return acc;
      },
      []
    );

    /*   Step 3: Check if any user has no attendance records for the selected date
         then mark them as Absent
    ********************************************* */
    const absentUsers = selectedUsers.filter(
      (userId) => !processedData.some((data) => data.userId === userId)
    );

    absentUsers.forEach((userId) => {
      const absentUser = allUsers.find((user) => user.userId === userId);
      if (absentUser) {
        let _weeklySchedule = JSON.parse(absentUser.weeklySchedule);

        let dayOfWeek = new Date(selectedDate).getDate();
        // Get the day name from the date
        let dayNameOfWeek = getDayNameByDate(new Date(selectedDate));

        let userInTime = _weeklySchedule[dayNameOfWeek]
          ? _weeklySchedule[dayNameOfWeek]?.inTime || "00:00"
          : "00:00";

        let userOutTime = _weeklySchedule[dayNameOfWeek]
          ? _weeklySchedule[dayNameOfWeek]?.outTime || "00:00"
          : "00:00";

        processedData.push({
          department: absentUser.department,
          userName: absentUser.name,
          userId: absentUser.userId,
          attendance: {
            inTime: null,
            outTime: null,
            userInTime,
            userOutTime,
            totalExpectedHours: getTotalHours(userInTime, userOutTime) || 0.0,
            formattedDate: getFormattedDate(selectedDate),
            dayOfWeek,
            inRecord: null,
            outRecord: null,
            dayNameOfWeek: getDayNameByDate(selectedDate),
            totalLateTime: 0.0,
            totalHours: 0.0,
            overTime: 0.0,
            type: isHoliday ? "Holiday" : "Absent",
            message: isHoliday ? "Holiday" : "Absent",
          },
          userData: absentUser,
        });
      }
    });

    console.log("Processed Attendance Data:", processedData);
    setSelectedReport(processedData);
  };

  // Get InTime, OutTime, Overtime, lateTime calculations
  const getTimeCalculations = (arr, selectedUser) => {
    const sortedArr = arr.sort(
      (a, b) => new Date(a.recordTime) - new Date(b.recordTime)
    );

    let userRules = JSON.parse(selectedUser?.rules);

    /* 
    userData = {
    "id": 3,
    "companyId": 3,
    "userId": 3,
    "deviceId": 1,
    "name": "TARIQ",
    "password": "",
    "role": "14",
    "cardno": "4634703",
    "userName": "Tariq Tahir",
    "badgeNo": "007",
    "relaxationMinutes": 15,
    "departmentId": null,
    "weeklySchedule": "{\"Monday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Tuesday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Wednesday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Thursday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Friday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Saturday\":{\"inTime\":\"\",\"outTime\":\"\",\"working\":false},\"Sunday\":{\"inTime\":\"\",\"outTime\":\"\",\"working\":false}}",
    rules:"{"overTime":{"name":"Overtime Calculation","description":"Calculate overtime after expected hours + overtimeAfterMinutes","isChecked":true},"lateTime":{"name":"Late Time Calculation","description":"Only calculate late time when inTime is greater than inTimeWithRelaxationFormatted. Count late time from userInTime when inTime is less than inTimeWithRelaxationFormatted","isChecked":true}}"
    "isNightOffice": false,
    "groupId": null,
    "createdAt": "2024-10-24T09:35:58.000Z",
    "updatedAt": "2024-11-13T07:42:02.000Z"
}
     */

    /**
     * After sorting records by Day
     * We only need 2 entries first and last entry.
     * First entry will always be inTime
     * Last entry will always be outTime
     * We also have user defined UserInTime and UserOutTime in UserData
     * We get overtime and late time comparing inTime-outTime with UserInTime-UserOutTime
     */
    const firstEntry = sortedArr.slice(0, 1);
    const lastEntry = sortedArr.slice(-1);

    const date = new Date(firstEntry[0].recordTime);
    let dayOfWeek = date.getDate();

    let formattedDate = firstEntry[0]?.formattedDate;
    let dayNameOfWeek = getDayNameByDate(date);

    /**
     * Get dayOfWeek from firstEntry (eg. Monday, Tuesday,.....)
     * Get inTime and outTime from selectedUser?.weeklySchedule[dayOfWeek]
     * Also check if selectedUser?.weeklySchedule[dayOfWeek].working === true
     */

    const weeklySchedule = JSON.parse(selectedUser?.weeklySchedule);
    const daySchedule = weeklySchedule[dayNameOfWeek] || {};

    let userInTime = daySchedule.inTime;
    let userOutTime = daySchedule.outTime;

    let inTime = firstEntry[0]?.formattedTime;
    let outTime = lastEntry[0]?.formattedTime;

    let relaxationMinutes = selectedUser?.relaxationMinutes;
    let overTimeAfterMinutes = selectedUser?.overtimeAfterMinutes;

    // console.log("date", formattedDate);

    /* Total Expected Hours
     **************** */
    let totalExpectedHoursInDecimal =
      getTotalHours(userInTime, userOutTime) || 0.0;

    /* Total Late Time
     **************** */
    let totalLateTimeValue =
      getTotalLateTime(
        inTime,
        outTime,
        userInTime,
        userOutTime,
        relaxationMinutes,
        userRules,
        selectedDevice
      ) || 0.0;

    /*   Total Hours Worked
     **************** */
    let totalHoursValue = getTotalHours(inTime, outTime) || 0.0;

    /*   Total OverTime
     **************** */
    let overTimeValue =
      getTotalOvertime(
        inTime,
        outTime,
        userInTime,
        userOutTime,
        overTimeAfterMinutes,
        userRules,
        selectedDevice
      ) || 0.0;

    let shouldCalculateOvertime =
      firstEntry[0].calculateOverTime === false ? false : true;

    // set overtime to 0 if calculateOvertime is false
    if (shouldCalculateOvertime === false) {
      overTimeValue = 0.0;
    }

    let shouldCalculateLateTime =
      firstEntry[0].calculateLateTime === false ? false : true;

    // set lateTime to 0 if calculateLateTime is false
    if (shouldCalculateLateTime === false) {
      totalLateTimeValue = 0.0;
    }
    // throw new Error("Not Implemented");

    return {
      inTime,
      outTime,
      shouldCalculateOvertime,
      shouldCalculateLateTime,
      formattedDate,
      dayOfWeek,
      inRecord: firstEntry[0].recordTime,
      outRecord: lastEntry[0].recordTime,
      dayNameOfWeek,
      userInTime,
      userOutTime,
      totalExpectedHours: totalExpectedHoursInDecimal,

      totalLateTime: inTime === outTime ? 0.0 : parseFloat(totalLateTimeValue),
      totalHours: inTime === outTime ? 0.0 : parseFloat(totalHoursValue),
      overTime: inTime === outTime ? 0.0 : parseFloat(overTimeValue),
    };
  };

  const getDate = (inputDate) => {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) {
      return null; // Invalid date
    }
    return date?.toISOString().slice(0, 10).replace("T", " ");
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (!deviceId) return;
      if (showModal) {
        getUsersDataByDeviceId(deviceId);
        GetDeviceById(deviceId);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [GetDeviceById, deviceId, getUsersDataByDeviceId, showModal]);

  // Update filtered users whenever search term, department filter, or all users change
  useEffect(() => {
    const filtered = allUsers.filter((user) => {
      const matchesSearch = user.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesDepartment =
        filterDepartment === "" || user.department === filterDepartment;
      return matchesSearch && matchesDepartment;
    });
    setFilteredUsers(filtered);

    // Update selectAll checkbox state based on filtered users
    if (filtered.length > 0) {
      const allFilteredSelected = filtered.every((user) =>
        selectedUsers.includes(user.userId)
      );
      setSelectAll(allFilteredSelected);
    }
  }, [allUsers, searchTerm, filterDepartment, selectedUsers]);

  useEffect(() => {
    // Set Initial Date to be yesterday
    const today = new Date();

    let _yesterday = getDate(today);
    setSelectedDate(_yesterday);
  }, []);

  return (
    <>
      <button
        onClick={(e) => {
          setShowModal(true);
        }}
        className="btn btn-primary ms-1"
      >
        Daily Attendance Report
      </button>
      <Modal
        contentClassName="bg-transparent"
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        size="lg"
      >
        <Modal.Body className="bg-white custom-border-radius px-4">
          <div>
            {loading && <Spinner />}
            <h3 className="mb-3">Generate Daily Attendance Report</h3>

            <div>
              <label className="form-label">Select Date</label>
              <input
                className="form-control"
                type="date"
                value={selectedDate || ""}
                onChange={handleSelectDate}
              />
            </div>
            <div className="mt-1">
              <span className="fw-bold">Current Time Zone :</span>{" "}
              {selectedDevice?.timezone} <br />
              <span className="fw-bold">Current Time :</span>{" "}
              {getCurTimeOfDeviceTimeZone(selectedDevice)}
            </div>
            {!allUsers && <Spinner />}

            {allUsers.length > 0 && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Select Users</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ maxWidth: "200px" }}
                    />
                    <select
                      className="form-select form-select-sm"
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      style={{ maxWidth: "180px" }}
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                >
                  <table className="table table-sm table-hover mb-0">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <tr>
                        <th style={{ width: "40px" }}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectAll}
                              onChange={handleSelectAllUsers}
                              id="selectAllCheckbox"
                            />
                            <label
                              className="form-check-label"
                              htmlFor="selectAllCheckbox"
                            ></label>
                          </div>
                        </th>
                        <th>Name</th>
                        <th>Department</th>
                        <th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.userId}>
                          <td>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedUsers.includes(user.userId)}
                                onChange={() => handleSelectUser(user.userId)}
                                id={`user-${user.userId}`}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`user-${user.userId}`}
                              ></label>
                            </div>
                          </td>
                          <td>{user.name}</td>
                          <td>{user.department}</td>
                          <td>{user.userId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 text-muted small">
                  {selectedUsers.length} of {allUsers.length} users selected
                </div>
              </div>
            )}

            <div className="text-end mt-3">
              <button
                onClick={(e) => {
                  setShowModal(false);
                }}
                className="btn btn-sm btn-secondary"
              >
                Close
              </button>
              <button
                onClick={GenerateReport}
                disabled={
                  !selectedDate || loading || selectedUsers.length === 0
                }
                type="button"
                className="btn btn-sm btn-info mx-1"
              >
                Get Records
              </button>
              {selectedReport && (
                <AttReportByDateBtn
                  selectedReport={selectedReport}
                  btnClasses={"btn btn-primary btn-sm"}
                />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
